import React, { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

// Design tokens aligned with WorkspaceDashboard.jsx
const G = "#0077B5";
const RED = "#D85A30";
const CARD = "#FFFFFF";
const BORDER = "rgba(0,0,0,0.1)";
const BG = "#f3f2ef";

const PLUG_TYPES = [
  { id: "evergreen",            label: "Evergreen",             emoji: "🌿", desc: "Runs every week, skips holidays, uses static content" },
  { id: "evergreen_rss",        label: "Evergreen RSS",         emoji: "📡", desc: "Runs every week, skips holidays, pulls from RSS feed" },
  { id: "announcement",         label: "Announcement",          emoji: "📢", desc: "One-time, time-sensitive" },
  { id: "holiday_announcement", label: "Holiday Announcement",  emoji: "🎉", desc: "One-time, posts ON a specific holiday" },
  { id: "limited_offer",        label: "Limited Offer",         emoji: "⏳", desc: "One-time, expires on a set date" },
  { id: "case_study",           label: "Case Study",            emoji: "📊", desc: "One-time or recurring customer story" },
  { id: "thought_leadership",   label: "Thought Leadership",    emoji: "💡", desc: "One-time or recurring founder POV" },
];

const PLUG_TYPE_LABELS = Object.fromEntries(PLUG_TYPES.map(t => [t.id, t.label]));

function authHeaders() {
  const st = localStorage.getItem("nectar_st");
  return { "Content-Type": "application/json", "X-Session-Token": st };
}

// ─── ImageUploader ──────────────────────────────────────────────────────────

function ImageUploader({ mediaUrl, onUploaded, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "gif"].includes(ext)) {
      setError("Only JPG, PNG, GIF allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large (max 10MB)");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const st = localStorage.getItem("nectar_st");
      const r = await fetch(API_BASE + "/app/api/upload/image", {
        method: "POST",
        headers: { "X-Session-Token": st },
        body: fd,
      });
      const d = await r.json();
      if (r.ok && d.url) {
        onUploaded(d.url);
      } else {
        setError(d.error || "Upload failed");
      }
    } catch {
      setError("Network error during upload");
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  if (mediaUrl) {
    return (
      <div style={{ marginTop: 10 }}>
        <img src={mediaUrl} alt="Plug media" style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 8, marginBottom: 8 }} />
        <button onClick={onRemove} style={{ background: "transparent", color: RED, border: "1px solid " + RED + "44", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Remove image
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragOver ? G : BORDER}`,
          borderRadius: 10,
          padding: "24px 16px",
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          background: dragOver ? "#EBF5FB" : "#fafaf9",
          transition: "all 0.15s",
          minHeight: 48,
        }}
      >
        {uploading ? (
          <div style={{ fontSize: 13, color: "#888" }}>Uploading…</div>
        ) : (
          <>
            <div style={{ fontSize: 20, marginBottom: 4 }}>📷</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#555" }}>Upload image</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Drag & drop or tap to browse</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>JPG, PNG, GIF · Max 10MB</div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif"
        style={{ display: "none" }}
        onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); }}
      />
      {error && <div style={{ fontSize: 12, color: RED, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// ─── PlugCard ────────────────────────────────────────────────────────────────

function PlugCard({ plug, onEdit, onDelete }) {
  const typeInfo = PLUG_TYPES.find(t => t.id === plug.plug_type) || { label: plug.plug_type, emoji: "•" };
  const isEvergreen = plug.plug_type === "evergreen";
  const expires = plug.expires_at ? new Date(plug.expires_at).toLocaleDateString() : null;
  const isExpired = plug.expires_at && new Date(plug.expires_at) < new Date();

  return (
    <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 12, padding: 16, marginBottom: 12 }}>
      {plug.media_url && (
        <img src={plug.media_url} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8, marginBottom: 12 }} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 18 }}>{typeInfo.emoji}</span>
        <span style={{ background: isEvergreen ? "#E1F5EE" : "#EEEDFE", color: isEvergreen ? "#0F6E56" : "#3C3489", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20 }}>
          {typeInfo.label}
        </span>
        {plug.rss_feed_url && (
          <span style={{ background: "#FAEEDA", color: "#633806", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20 }}>
            📡 RSS
          </span>
        )}
        {plug.is_recurring && !isEvergreen && (
          <span style={{ background: "#E6F1FB", color: "#0C447C", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20 }}>
            Recurring
          </span>
        )}
        {isExpired && (
          <span style={{ background: "#FAE4E4", color: "#8B1A1A", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20 }}>
            Expired
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#999" }}>
          {plug.voice_direction === "as_company" ? "Staff mode" : "Ambassador mode"}
        </span>
      </div>

      <div style={{ fontSize: 13, color: "#333", lineHeight: 1.5, marginBottom: 10, whiteSpace: "pre-wrap" }}>
        {(plug.base_caption || "").slice(0, 280)}{(plug.base_caption || "").length > 280 ? "…" : ""}
      </div>

      {plug.destination_url && (
        <div style={{ fontSize: 11, color: "#888", marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          🔗 {plug.destination_url}
        </div>
      )}

      {expires && (
        <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>
          Expires: {expires}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => onEdit(plug)}
          data-testid={`btn-plug-edit-${plug.id}`}
          style={{ background: "transparent", color: G, border: "0.5px solid " + G, borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Edit
        </button>
        <button
          onClick={() => onDelete(plug)}
          data-testid={`btn-plug-delete-${plug.id}`}
          style={{ background: "transparent", color: RED, border: "0.5px solid " + RED + "44", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── PlugCreationModal ───────────────────────────────────────────────────────

function PlugCreationModal({ workspaceId, editingPlug, onClose, onSaved }) {
  const isEditing = !!editingPlug;
  const [plugType, setPlugType] = useState(editingPlug?.plug_type || "evergreen");
  const [baseCaption, setBaseCaption] = useState(editingPlug?.base_caption || "");
  const [destinationUrl, setDestinationUrl] = useState(editingPlug?.destination_url || "");
  const [mediaUrl, setMediaUrl] = useState(editingPlug?.media_url || "");
  const [rssFeedUrl, setRssFeedUrl] = useState(editingPlug?.rss_feed_url || "");
  const [voiceDirection, setVoiceDirection] = useState(editingPlug?.voice_direction || "about_company");
  const [expiresAt, setExpiresAt] = useState(editingPlug?.expires_at ? editingPlug.expires_at.slice(0, 10) : "");
  const [isRecurring, setIsRecurring] = useState(!!editingPlug?.is_recurring);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSamples, setPreviewSamples] = useState(null);

  // RSS test state
  const [rssTesting, setRssTesting] = useState(false);
  const [rssPreview, setRssPreview] = useState(null);

  const isEvergreen = plugType === "evergreen" || plugType === "evergreen_rss";
  const isEvergreenRss = plugType === "evergreen_rss";
  const canRecur = plugType === "case_study" || plugType === "thought_leadership";

  useEffect(() => {
    if (!canRecur && !isEvergreen && isRecurring) setIsRecurring(false);
  }, [plugType, canRecur, isEvergreen, isRecurring]);

  const runPreview = async () => {
    if (isEvergreenRss && !rssPreview) {
      setError("Validate your RSS feed first.");
      return;
    }
    if (!baseCaption.trim() || baseCaption.length < 20) {
      setError(isEvergreenRss ? "RSS feed did not return enough content. Try a different feed." : "Write at least 20 characters before previewing.");
      return;
    }
    setError(null);
    setPreviewLoading(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/plug/preview", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ base_caption: baseCaption, voice_direction: voiceDirection }),
      });
      const d = await r.json();
      if (r.ok) setPreviewSamples(d.samples);
      else setError(d.error || "Preview failed");
    } catch {
      setError("Network error during preview");
    }
    setPreviewLoading(false);
  };

  const testRss = async () => {
    if (!rssFeedUrl.trim()) return;
    setRssTesting(true);
    setRssPreview(null);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/plug/test-rss", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ rss_feed_url: rssFeedUrl }),
      });
      const d = await r.json();
      if (r.ok) {
        setRssPreview(d);
        if (isEvergreenRss && d.latest && d.latest.length > 0) {
          const article = d.latest[0];
          const caption = article.summary ? `${article.title}\n\n${article.summary}` : article.title;
          setBaseCaption(caption.slice(0, 3000));
          if (article.link) setDestinationUrl(article.link);
        }
      }
      else setError(d.error || "RSS test failed");
    } catch {
      setError("Network error fetching RSS");
    }
    setRssTesting(false);
  };

  const save = async () => {
    setError(null);
    if (isEvergreenRss && !rssPreview) {
      setError("Validate your RSS feed first.");
      return;
    }
    if (!baseCaption.trim() || baseCaption.length < 20) {
      setError(isEvergreenRss ? "RSS feed did not return enough content. Try a different feed." : "Caption must be at least 20 characters.");
      return;
    }
    if (!previewSamples) {
      setError("You must generate sample previews before saving. Click 'Generate 3 sample tones' first.");
      return;
    }
    setSaving(true);
    try {
      let url, method, body;
      if (isEditing) {
        url = `${API_BASE}/app/api/workspace/${workspaceId}/plug/${editingPlug.id}`;
        method = "PATCH";
        body = {
          base_caption: baseCaption,
          voice_direction: voiceDirection,
          destination_url: destinationUrl || null,
          media_url: mediaUrl || null,
          is_recurring: canRecur ? isRecurring : (isEvergreen ? true : false),
        };
        if (rssFeedUrl && isEvergreenRss) body.rss_feed_url = rssFeedUrl;
        if (expiresAt && !isEvergreen) body.expires_at = new Date(expiresAt).toISOString();
      } else if (isEvergreen) {
        url = `${API_BASE}/app/api/workspace/${workspaceId}/plug/create-evergreen`;
        method = "POST";
        body = {
          base_caption: baseCaption,
          voice_direction: voiceDirection,
          destination_url: destinationUrl || null,
          media_url: mediaUrl || null,
        };
        if (rssFeedUrl && isEvergreenRss) body.rss_feed_url = rssFeedUrl;
      } else {
        url = `${API_BASE}/app/api/workspace/${workspaceId}/plug/create-onetime`;
        method = "POST";
        body = {
          plug_type: plugType,
          base_caption: baseCaption,
          voice_direction: voiceDirection,
          destination_url: destinationUrl || null,
          media_url: mediaUrl || null,
          is_recurring: canRecur ? isRecurring : false,
        };
        if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      }

      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || "Save failed");
        setSaving(false);
        return;
      }
      onSaved(d.plug);
    } catch (e) {
      setError("Network error saving plug");
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "20px 28px 12px", borderBottom: "0.5px solid " + BORDER, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{isEditing ? "Edit plug" : "Create plug"}</div>
          <button onClick={onClose} data-testid="btn-plug-modal-close" style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "#888" }}>×</button>
        </div>

        <div style={{ padding: 28 }}>
          {/* Step 1: plug type */}
          {!isEditing && (
            <section style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 1 — Choose type</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                {PLUG_TYPES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setPlugType(t.id)}
                    data-testid={`btn-plug-type-${t.id}`}
                    style={{
                      background: plugType === t.id ? "#E6F1FB" : "#fff",
                      border: plugType === t.id ? "2px solid " + G : "1px solid " + BORDER,
                      borderRadius: 10, padding: "12px 14px", textAlign: "left", cursor: "pointer",
                      fontFamily: "inherit",
                    }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{t.emoji} {t.label}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Step 2: content */}
          <section style={{ marginBottom: 24 }}>
            {isEvergreenRss ? (
              <>
                <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 2 — Connect RSS feed</div>
                <label style={{ fontSize: 13, color: "#555", display: "block", marginBottom: 4 }}>
                  RSS feed URL — system pulls the latest post weekly
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={rssFeedUrl}
                    onChange={e => { setRssFeedUrl(e.target.value); setRssPreview(null); setBaseCaption(""); setDestinationUrl(""); }}
                    data-testid="input-plug-rss"
                    placeholder="https://your-blog.com/feed"
                    style={{ flex: 1, padding: "10px 12px", borderRadius: 6, border: "1px solid " + BORDER, fontSize: 13, boxSizing: "border-box" }}
                  />
                  <button onClick={testRss} disabled={!rssFeedUrl.trim() || rssTesting} data-testid="btn-plug-rss-test" style={{ background: "transparent", color: G, border: "1px solid " + G, borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                    {rssTesting ? "Testing…" : "Validate"}
                  </button>
                </div>
                {rssPreview && rssPreview.latest && rssPreview.latest.length > 0 && (
                  <div style={{ marginTop: 10, padding: 12, background: "#E8F5E9", borderRadius: 8, fontSize: 12, border: "1px solid #A5D6A7" }}>
                    <div style={{ fontWeight: 600, marginBottom: 6, color: "#2E7D32" }}>✅ Feed validated — {rssPreview.feed_title || "Feed"}</div>
                    <div style={{ color: "#333", marginBottom: 4 }}>Latest article: <strong>{rssPreview.latest[0].title}</strong></div>
                    {rssPreview.latest[0].summary && (
                      <div style={{ color: "#666", fontSize: 11, marginBottom: 4, lineHeight: 1.4 }}>{rssPreview.latest[0].summary.slice(0, 200)}{rssPreview.latest[0].summary.length > 200 ? "…" : ""}</div>
                    )}
                    <div style={{ color: "#999", fontSize: 11 }}>{rssPreview.item_count} items in feed · This content will be used for preview below</div>
                  </div>
                )}
                {!rssPreview && !rssTesting && rssFeedUrl.trim() && (
                  <div style={{ fontSize: 12, color: "#888", fontStyle: "italic", marginTop: 8 }}>Click Validate to verify your feed and load the latest article for preview.</div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 2 — Write content</div>
                <label style={{ fontSize: 13, color: "#555", display: "block", marginBottom: 4 }}>Base caption</label>
                <textarea
                  value={baseCaption}
                  onChange={e => setBaseCaption(e.target.value)}
                  data-testid="input-plug-caption"
                  rows={5}
                  placeholder="The plug text admin writes — each team member gets their own personalized version based on role, MBTI, and voice."
                  maxLength={3000}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid " + BORDER, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5 }}
                />
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{baseCaption.length} / 3000</div>

                <label style={{ fontSize: 13, color: "#555", display: "block", marginTop: 14, marginBottom: 4 }}>Destination URL</label>
                <input
                  value={destinationUrl}
                  onChange={e => setDestinationUrl(e.target.value)}
                  data-testid="input-plug-destination"
                  placeholder="https://brandhives.com/product"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid " + BORDER, fontSize: 13, boxSizing: "border-box" }}
                />

                {/* Image upload */}
                <label style={{ fontSize: 13, color: "#555", display: "block", marginTop: 14, marginBottom: 0 }}>Media (optional)</label>
                <ImageUploader
                  mediaUrl={mediaUrl}
                  onUploaded={(url) => setMediaUrl(url)}
                  onRemove={() => setMediaUrl("")}
                />
              </>
            )}
          </section>

          {/* Step 3: voice direction */}
          <section style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 3 — Voice direction</div>
            <div className="plug-voice-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button
                onClick={() => setVoiceDirection("as_company")}
                data-testid="btn-plug-voice-as-company"
                style={{ background: voiceDirection === "as_company" ? "#E6F1FB" : "#fff", border: voiceDirection === "as_company" ? "2px solid " + G : "1px solid " + BORDER, borderRadius: 10, padding: "14px 16px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Staff mode</div>
                <div style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>Members speak as the company — "We built...", "Our team..."</div>
              </button>
              <button
                onClick={() => setVoiceDirection("about_company")}
                data-testid="btn-plug-voice-about-company"
                style={{ background: voiceDirection === "about_company" ? "#E6F1FB" : "#fff", border: voiceDirection === "about_company" ? "2px solid " + G : "1px solid " + BORDER, borderRadius: 10, padding: "14px 16px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Ambassador mode</div>
                <div style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>Members speak about the company — "The team at X built..."</div>
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>Each member's role_type can override this — founders keep 1st person, employees default to 3rd, ambassadors always 3rd.</div>
          </section>

          {/* Step 4: preview (MANDATORY) */}
          <section style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>
              Step 4 — Preview 3 sample rewrites <span style={{ color: RED }}>(required)</span>
            </div>
            <button onClick={runPreview} disabled={previewLoading || baseCaption.length < 20} data-testid="btn-plug-preview" style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: 10, opacity: (previewLoading || baseCaption.length < 20) ? 0.6 : 1 }}>
              {previewLoading ? "Generating samples…" : "Generate 3 sample tones"}
            </button>
            {!previewSamples && !previewLoading && (
              <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>You must preview samples before saving.</div>
            )}
            {previewSamples && (
              <div className="plug-sample-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {["analytical", "casual", "storytelling"].map(tone => (
                  <div key={tone} style={{ background: BG, borderRadius: 8, overflow: "hidden" }}>
                    {mediaUrl && (
                      <img src={mediaUrl} alt="" style={{ width: "100%", height: 100, objectFit: "cover" }} />
                    )}
                    <div style={{ padding: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: G, textTransform: "uppercase", marginBottom: 6 }}>{tone}</div>
                      <div style={{ fontSize: 12, color: "#333", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{previewSamples[tone] || "(empty)"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {previewSamples && (
              <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
                These are sample tone ranges. Each team member will get their own version based on their role, MBTI, and voice preference.
              </div>
            )}
          </section>

          {/* Step 5: schedule */}
          {!isEvergreen && (
            <section style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 5 — Schedule</div>
              <label style={{ fontSize: 13, color: "#555", display: "block", marginBottom: 4 }}>Expires on</label>
              <input
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
                data-testid="input-plug-expires"
                style={{ padding: "10px 12px", borderRadius: 6, border: "1px solid " + BORDER, fontSize: 13 }}
              />
              {canRecur && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: "#555", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={e => setIsRecurring(e.target.checked)}
                    data-testid="input-plug-recurring"
                  />
                  Recurring — this plug cycles back into the weekly rotation
                </label>
              )}
            </section>
          )}
          {isEvergreen && (
            <section style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>Step 5 — Schedule</div>
              <div style={{ fontSize: 13, color: "#555" }}>
                Runs every week · Auto-skips observed holidays · {isEvergreenRss ? "Pulls latest RSS post" : "Uses static content"}
              </div>
            </section>
          )}

          {error && (
            <div style={{ background: "#FAE4E4", color: "#8B1A1A", padding: "10px 14px", borderRadius: 6, fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: "16px 28px", borderTop: "0.5px solid " + BORDER, display: "flex", justifyContent: "flex-end", gap: 8, background: BG, borderRadius: "0 0 12px 12px" }}>
          <button onClick={onClose} data-testid="btn-plug-cancel" style={{ background: "transparent", border: "1px solid " + BORDER, borderRadius: 6, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving || !baseCaption.trim() || !previewSamples} data-testid="btn-plug-save" style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (saving || !baseCaption.trim() || !previewSamples) ? 0.6 : 1 }}>
            {saving ? "Saving…" : (isEditing ? "Save changes" : "Create plug")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main section ────────────────────────────────────────────────────────────

export default function PlugManagementSection({ workspaceId }) {
  const [plugs, setPlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlug, setEditingPlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API_BASE + "/app/api/workspace/" + workspaceId + "/plugs", { headers: authHeaders() });
      const d = await r.json();
      if (r.ok) setPlugs(d.plugs || []);
      else setError(d.error || "Failed to load plugs");
    } catch {
      setError("Network error");
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingPlug(null); setShowModal(true); };
  const openEdit = (plug) => { setEditingPlug(plug); setShowModal(true); };

  const handleDelete = async (plug) => {
    if (!window.confirm(`Delete "${PLUG_TYPE_LABELS[plug.plug_type] || plug.plug_type}" plug?`)) return;
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/plug/${plug.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (r.ok) load();
      else alert("Delete failed");
    } catch {
      alert("Network error");
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingPlug(null);
    load();
  };

  const evergreen = plugs.filter(p => p.plug_type === "evergreen");
  const oneTime = plugs.filter(p => p.plug_type !== "evergreen");

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>Team plugs</div>
        <button
          onClick={openCreate}
          data-testid="btn-plug-create"
          style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          + Create plug
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Loading plugs…</div>
      ) : error ? (
        <div style={{ padding: 20, textAlign: "center", color: RED }}>{error}</div>
      ) : (
        <>
          {/* Evergreen section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>
              Evergreen (recurring weekly)
            </div>
            {evergreen.length === 0 ? (
              <div
                data-testid="plug-evergreen-onboarding-banner"
                style={{
                  background: "linear-gradient(135deg, #FFFBEB 0%, #FDF4DD 100%)",
                  border: "1.5px solid #FDE68A",
                  borderRadius: 12,
                  padding: "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#633806", marginBottom: 6 }}>
                    🌿 Set up your default evergreen plug
                  </div>
                  <div style={{ fontSize: 13, color: "#78350F", lineHeight: 1.5 }}>
                    Every workspace needs one evergreen plug — it runs every week automatically,
                    skips holidays, and can auto-pull from an RSS feed. You can add one-time plugs
                    (announcements, case studies, etc.) later.
                  </div>
                </div>
                <button
                  onClick={() => { setEditingPlug(null); setShowModal(true); }}
                  data-testid="btn-plug-evergreen-setup"
                  style={{
                    background: G,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 22px",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    minHeight: 44,
                  }}>
                  Set up now →
                </button>
              </div>
            ) : (
              evergreen.map(p => <PlugCard key={p.id} plug={p} onEdit={openEdit} onDelete={handleDelete} />)
            )}
          </div>

          {/* One-time plugs */}
          <div>
            <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 10 }}>
              One-time plugs
            </div>
            {oneTime.length === 0 ? (
              <div style={{ padding: "14px 16px", background: BG, borderRadius: 10, fontSize: 13, color: "#888", textAlign: "center" }}>
                No one-time plugs active.
              </div>
            ) : (
              oneTime.map(p => <PlugCard key={p.id} plug={p} onEdit={openEdit} onDelete={handleDelete} />)
            )}
          </div>
        </>
      )}

      {showModal && (
        <PlugCreationModal
          workspaceId={workspaceId}
          editingPlug={editingPlug}
          onClose={() => { setShowModal(false); setEditingPlug(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
